import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Tag, Users, Headphones, DollarSign, Heart, Globe, Sparkles, UserPlus, Package, Wallet, Check, X } from 'lucide-react';
import Navbar from './components/ui/navbar';

const MainApp: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'Featured Products',
      color: 'white',
      bgColor: 'bg-gradient-to-br from-stone-900 via-stone-800 to-stone-700',
      image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&h=600&fit=crop',
    },
    {
      title: 'Handcrafted Goods',
      color: 'orange',
      bgColor: 'bg-orange-500',
      image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=800&h=600&fit=crop',
    },
    {
      title: 'Unique Designs',
      color: 'purple',
      bgColor: 'bg-purple-600',
      image: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?w=800&h=600&fit=crop',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "/create-space" },
    },
  };

  return (
    <div className="scroll-snap-container">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 sm:pt-6 px-2 sm:px-4 pointer-events-none animate-navbar-float">
        <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[20px] sm:rounded-[40px] px-3 sm:px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <Navbar {...navbarConfig} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="scroll-snap-section flex items-center justify-center">
        <div className="page-content-wrapper bg-[#f5f3f0] text-black relative w-full flex items-center justify-center min-h-screen pt-24">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                NYC's Creator<br />Marketplace
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl font-light max-w-xl">
                Zero commission. Full profit. Built by artists for artists.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/products')}
                  className="bg-black text-[#f5f3f0] px-8 py-4 rounded-full hover:bg-black transition-colors font-semibold text-lg"
                >
                  Explore Collection
                </button>
                <button className="bg-transparent border-2 border-black text-black px-8 py-4 rounded-full hover:bg-[#f5f3f0] transition-colors font-semibold text-lg">
                  Start Selling
                </button>
              </div>

              {/* Stats Section */}
              <div className="grid grid-cols-3 gap-3 sm:gap-6 pt-8">
                {/* 5K+ Creators Card */}
                <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-6 md:p-8 hover:bg-[#f5f3f0] transition-all duration-300">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">5K+</div>
                  <div className="text-xs sm:text-sm md:text-base font-light text-black">Creators</div>
                </div>

                {/* 0% Fees Card */}
                <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-6 md:p-8 hover:bg-[#f5f3f0] transition-all duration-300">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">5%</div>
                  <div className="text-xs sm:text-sm md:text-base font-light text-black">Fees</div>
                </div>

                {/* $2M+ Card */}
                <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-6 md:p-8 hover:bg-[#f5f3f0] transition-all duration-300">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2">$2M+</div>
                  <div className="text-xs sm:text-sm md:text-base font-light text-black">Earnings</div>
                </div>
              </div>
            </div>

            {/* Right Column - Picture Card */}
            <div className="relative lg:scale-110 mt-8 lg:mt-0">
              <div className="relative w-full aspect-[4/3] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-700 hover:scale-105">
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#f5f3f0] mb-3 sm:mb-4">
                    {slides[currentSlide].title}
                  </h3>
                  {/* Navigation Dots */}
                  <div className="flex gap-2">
                    {slides.map((_, dotIndex) => (
                      <button
                        key={dotIndex}
                        onClick={() => setCurrentSlide(dotIndex)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          dotIndex === currentSlide
                            ? 'bg-[#f5f3f0] w-8'
                            : 'bg-[#f5f3f0] hover:bg-[#f5f3f0] w-2'
                        }`}
                        aria-label={`Go to slide ${dotIndex + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Second Page - By NYC Artists Section */}
      <section className="scroll-snap-section flex items-center justify-center">
        <div className="page-content-wrapper bg-gradient-to-br from-stone-900 via-stone-800 to-stone-700 w-full flex items-center justify-center min-h-screen pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-normal text-[#f5f3f0] mb-4">
              By NYC artists, for artists everywhere
            </h2>
          </div>

          {/* Three Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {/* Zero Fees Card */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-3xl p-8 transition-all duration-300">
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <DollarSign width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Zero Fees</h3>
              <p className="text-lg text-black leading-relaxed">
                Keep 100% of your earnings. No commission, no hidden charges.
              </p>
            </div>

            {/* Community Card */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-3xl p-8 transition-all duration-300">
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Users width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Community</h3>
              <p className="text-lg text-black leading-relaxed">
                5,000+ NYC creators building something real together.
              </p>
            </div>

            {/* Authentic Card */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-3xl p-8 transition-all duration-300">
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Sparkles width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Authentic</h3>
              <p className="text-lg text-black leading-relaxed">
                Only verified handcrafted goods. No mass production.
              </p>
            </div>
          </div>

          {/* Story Section - Smaller with Orange */}
          <div className="bg-orange-500 rounded-2xl p-8 md:p-10 text-[#f5f3f0] max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Tired of 15-30% marketplace fees?
            </h3>
            <p className="text-lg md:text-xl font-light leading-relaxed">
              Founded in 2024 by NYC artists who believed creators deserved better. No hidden fees. No surprises. Just 100% support for your craft.
            </p>
          </div>
        </div>
        </div>
      </section>

      {/* Third Page - Why Choose Fleait */}
      <section className="scroll-snap-section flex items-center justify-center">
        <div className="page-content-wrapper bg-[#f5f3f0] w-full flex items-center justify-center min-h-screen pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-black mb-4">
              Why Choose Fleait?
            </h2>
            <p className="text-xl text-black max-w-3xl mx-auto">
              Built for creators who want more from their marketplace
            </p>
          </div>

          {/* Three Colored Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Zero Fees Card - Orange */}
            <div
              className="rounded-3xl p-8 md:p-10 transition-all duration-300"
              style={{ backgroundColor: 'rgb(255, 107, 53)' }}
            >
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <DollarSign width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#f5f3f0] mb-4">Zero Fees</h3>
              <p className="text-lg text-[#f5f3f0] leading-relaxed">
                Keep every penny you earn. No hidden charges, no commissions. Just pure profit for creators.
              </p>
            </div>

            {/* Local Love Card - Purple */}
            <div
              className="rounded-3xl p-8 md:p-10 transition-all duration-300"
              style={{ backgroundColor: 'rgb(168, 85, 247)' }}
            >
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Heart width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#f5f3f0] mb-4">Local Love</h3>
              <p className="text-lg text-[#f5f3f0] leading-relaxed">
                Made by New Yorkers, for everyone. Every item tells a story of hustle, passion, and NYC pride.
              </p>
            </div>

            {/* Global Reach Card - Dark */}
            <div
              className="rounded-3xl p-8 md:p-10 transition-all duration-300"
              style={{ backgroundColor: 'rgb(41, 37, 36)' }}
            >
              <div className="mb-6">
                <div className="w-20 h-20 bg-black rounded-3xl flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Globe width="36" height="36" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#f5f3f0] mb-4">Global Reach</h3>
              <p className="text-lg text-[#f5f3f0] leading-relaxed">
                Small shop, big audience. Compete with major brands and reach customers worldwide.
              </p>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Fourth Page - Three Simple Steps */}
      <section className="scroll-snap-section flex items-center justify-center">
        <div className="page-content-wrapper bg-gradient-to-br from-stone-900 via-stone-800 to-stone-700 w-full flex items-center justify-center min-h-screen pt-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#f5f3f0] mb-3">
              Three simple steps to start earning
            </h2>
          </div>

          {/* Three Steps Cards - Smaller */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {/* Step 1 */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-2xl p-6 transition-all duration-300">
              <div className="mb-4">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-4 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <UserPlus width="28" height="28" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
                <div className="text-4xl font-bold text-black mb-3">1</div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Create Space</h3>
              <p className="text-base text-black leading-relaxed">
                Set up your profile in minutes.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-2xl p-6 transition-all duration-300">
              <div className="mb-4">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-4 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Package width="28" height="28" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
                <div className="text-4xl font-bold text-black mb-3">2</div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Start Selling</h3>
              <p className="text-base text-black leading-relaxed">
                List unlimited products.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-2xl p-6 transition-all duration-300">
              <div className="mb-4">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-4 shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
                  <Wallet width="28" height="28" strokeWidth={2} className="text-[#f5f3f0]" aria-hidden="true" />
                </div>
                <div className="text-4xl font-bold text-black mb-3">3</div>
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Keep 100%</h3>
              <p className="text-base text-black leading-relaxed">
                Get paid instantly. All yours.
              </p>
            </div>
          </div>

          {/* Comparison Section - Smaller */}
          <div className="mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-[#f5f3f0] text-center mb-6">
              Fleait vs Others
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {/* Fleait Card */}
              <div className="bg-[#f5f3f0] rounded-xl p-5">
                <h4 className="text-lg font-bold text-black mb-4">Fleait</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center p-0.5">
                      <Check className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">0% commission</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center p-0.5">
                      <Check className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">Instant payouts</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center p-0.5">
                      <Check className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">Free listings</span>
                  </li>
                </ul>
              </div>

              {/* Others Card */}
              <div className="bg-[#f5f3f0] backdrop-blur-lg rounded-xl p-5">
                <h4 className="text-lg font-bold text-black mb-4">Others</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center p-0.5">
                      <X className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">15-30% fees</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center p-0.5">
                      <X className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">7-14 day delays</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center p-0.5">
                      <X className="w-full h-full text-[#f5f3f0]" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-black">Listing fees</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Fifth Page - Ready to Join + Footer */}
      <section className="scroll-snap-section bg-[#f5f3f0] text-black flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-16 pt-32">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Ready to Join the Movement?
            </h2>
            <p className="text-lg md:text-xl font-light mb-3">
              Join 5,000+ NYC creators selling with 3 - 5% fees.
            </p>
            <p className="text-xl md:text-2xl font-semibold mb-8">
              Your turn to shine.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/create-space')}
                className="bg-black text-[#f5f3f0] px-8 py-4 rounded-full hover:bg-black transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                Create Your Space
              </button>
              <button
                onClick={() => navigate('/products')}
                className="bg-transparent border-2 border-black text-black px-8 py-4 rounded-full hover:bg-[#f5f3f0] transition-colors font-semibold text-lg"
              >
                Browse Marketplace
              </button>
            </div>
          </div>
        </div>

        {/* Footer Content */}
        <div className="border-t border-black mt-auto bg-[rgba(69,138,255,0.8)] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-2xl font-logo mb-4 text-black">Fleait</h3>
                <p className="text-black text-lg">
                  Curating luxury experiences, one piece at a time.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4 text-black">Quick Links</h4>
                <ul className="space-y-2">
                  <li><a href="/" className="text-black hover:text-black transition-colors">Home</a></li>
                  <li><a href="/products" className="text-black hover:text-black transition-colors">Categories</a></li>
                  <li><a href="/about" className="text-black hover:text-black transition-colors">About</a></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Contact</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4 text-black">Support</h4>
                <ul className="space-y-2">
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Help Center</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Shipping</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Returns</button></li>
                  <li><button onClick={() => {}} className="text-black hover:text-black transition-colors text-left">Privacy</button></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-black pt-6 text-center">
              <p className="text-black text-sm">© 2024 Fleait. All rights reserved.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainApp;